/* Created by Interactive 63 - https://interactive63.com */
/**
 * First-run tips: three dismissible coach marks shown one at a time after the first import,
 * each anchored to the part of the interface it describes. Once they are finished or skipped
 * the choice is remembered, and the Help menu can show them again.
 */
import { readStorage, writeStorage } from './storage';
import { h } from './ui/dom';

const STORAGE_KEY = 'svg-tempo:onboarding';
const MARGIN = 12;

interface Tip {
  text: string;
  anchor: string;
  /** Where the card sits relative to its anchor. */
  placement: 'inside-top-left' | 'inside-top-right' | 'below-right';
}

const TIPS: Tip[] = [
  {
    text: 'Select a part of your SVG here or in Layers.',
    anchor: '.stage-viewport',
    placement: 'inside-top-left',
  },
  {
    text: 'Click a diamond to add a keyframe, move the playhead, then change the value.',
    anchor: '.inspector',
    placement: 'inside-top-right',
  },
  {
    text: 'Export gives you HTML and CSS to paste into any site.',
    anchor: '.topbar .primary',
    placement: 'below-right',
  },
];

export class Onboarding {
  private card: HTMLElement | null = null;
  private index = 0;

  /** Shows the tips if they have not been finished before. */
  startIfNew(): void {
    if (readStorage(STORAGE_KEY) === 'done' || this.card) return;
    this.start();
  }

  /** Shows the tips from the first one, whether or not they were seen before. */
  start(): void {
    this.dismiss();
    this.index = 0;
    this.show();
  }

  /** Removes the card without recording anything. */
  dismiss(): void {
    this.card?.remove();
    this.card = null;
  }

  /** Records that the tips are finished and hides the card. */
  private finish(): void {
    writeStorage(STORAGE_KEY, 'done');
    this.dismiss();
  }

  /** Builds and places the card for the current tip. */
  private show(): void {
    this.card?.remove();
    const tip = TIPS[this.index];
    if (!tip) {
      this.finish();
      return;
    }
    const last = this.index === TIPS.length - 1;
    const next = h('button', { type: 'button', class: 'primary' }, last ? 'Got it' : 'Next');
    next.addEventListener('click', () => {
      if (last) {
        this.finish();
      } else {
        this.index += 1;
        this.show();
      }
    });
    const skip = h('button', { type: 'button', class: 'link-button' }, 'Skip tips');
    skip.addEventListener('click', () => {
      this.finish();
    });
    const card = h(
      'div',
      {
        class: 'coach-mark',
        role: 'dialog',
        'aria-label': `Tip ${this.index + 1} of ${TIPS.length}`,
      },
      h('p', null, tip.text),
      h(
        'div',
        { class: 'coach-actions' },
        h('span', { class: 'coach-step' }, `${this.index + 1} of ${TIPS.length}`),
        skip,
        next,
      ),
    );
    card.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.finish();
      }
    });
    document.body.append(card);
    this.card = card;
    this.place(card, tip);
    next.focus();
  }

  /** Positions the card against its anchor, keeping it fully inside the window. */
  private place(card: HTMLElement, tip: Tip): void {
    const anchor = document.querySelector(tip.anchor)?.getBoundingClientRect();
    const width = card.offsetWidth;
    const height = card.offsetHeight;
    let left = window.innerWidth - width - MARGIN;
    let top = MARGIN;
    if (anchor) {
      if (tip.placement === 'inside-top-left') {
        left = anchor.left + MARGIN;
        top = anchor.top + MARGIN;
      } else if (tip.placement === 'inside-top-right') {
        left = anchor.right - width - MARGIN;
        top = anchor.top + MARGIN;
      } else {
        left = anchor.right - width;
        top = anchor.bottom + MARGIN;
      }
    }
    card.style.left = `${Math.max(MARGIN, Math.min(window.innerWidth - width - MARGIN, left))}px`;
    card.style.top = `${Math.max(MARGIN, Math.min(window.innerHeight - height - MARGIN, top))}px`;
  }
}
